import { ComponentChildren } from 'preact';

export type SegmentedControlOption<T = string> = {
  value: T;
  label: ComponentChildren;
  tooltip?: string;
};

export type SegmentedControlProps<T = string> = {
  /** The currently selected value */
  value: T;
  /** Array of options to display */
  options: SegmentedControlOption<T>[];
  /** Callback when selection changes */
  onChange: (value: T) => void;
  /** Optional class name for the container */
  class?: string;
};

/**
 * A segmented control component for switching between multiple options.
 * Provides a consistent design for toggle groups across the application.
 */
export function SegmentedControl<T = string>({
  value,
  options,
  onChange,
  class: className = '',
}: SegmentedControlProps<T>) {
  return (
    <div class={`inline-flex bg-gray-200 rounded-md p-1 ${className}`}>
      {options.map((option) => (
        <button
          key={String(option.value)}
          class={`px-2 py-1 text-xs font-medium rounded transition-colors ${
            value === option.value
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
          onClick={() => onChange(option.value)}
          title={option.tooltip}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
